import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SettingsIcon from '@mui/icons-material/Settings';
import { getDay, isValid, parseISO } from 'date-fns';
import { AxiosError } from 'axios';

import PageHeader from '../../../sharedModules/layout/PageHeader';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { CorporateLocation } from '../../../sharedModules/utils/locationStore';
import { useCorporateLocation } from '../../../sharedModules/utils/useCorporateLocation';
import EmailTemplateEditor from '../../shared/EmailTemplateEditor';
import EmailTemplatePreview from '../../shared/EmailTemplatePreview';
import { collectionCandidatesQueryKey } from '../../clients/hooks/useCollectionClients';
import {
  useCollectionConfig,
  useUpdateCollectionBucket,
  useUpdateCollectionConfig,
} from '../hooks/useCollectionConfig';
import {
  CollectionConfig,
  CollectionEmailTemplate,
  CollectionFrequency,
  CollectionTone,
  FREQUENCIES,
  FREQUENCY_LABELS,
  MONTH_DAYS,
  SCHEDULE_DAYS,
  TEMPLATE_VARIABLES,
  TONE_LABELS,
  TONES,
} from '../model';
import { TONE_COLOR_BY_KEY } from '../../shared/toneColors';
import { bucketClientCountText } from '../bucketToggleLabel';
import { residentialToggleImpactText } from '../residentialToggleLabel';

// SCHEDULE_DAYS is Monday-anchored; date-fns getDay returns 0 = Sunday.
const scheduleDayFromJs = (jsDay: number): string => SCHEDULE_DAYS[(jsDay + 6) % 7];

const weekdayMatchesAnchor = (scheduleDay: string | null, anchorDate: string | null): boolean => {
  if (!scheduleDay || !anchorDate) return true;
  const parsed = parseISO(anchorDate);
  if (!isValid(parsed)) return true;
  return scheduleDayFromJs(getDay(parsed)) === scheduleDay;
};

const normalizeConfig = (raw: CollectionConfig): CollectionConfig => ({
  ...raw,
  include_residential: raw.include_residential ?? false,
  residential_client_count: raw.residential_client_count ?? 0,
  sending_enabled: raw.sending_enabled ?? false,
  past_due_threshold_days: raw.past_due_threshold_days ?? 30,
  past_due_grace_days: raw.past_due_grace_days ?? 0,
  bucket_client_counts: raw.bucket_client_counts ?? { past_due: 0, friendly: 0, second_notice: 0, urgent: 0 },
});

const buildSavePayload = (config: CollectionConfig) => ({
  config: {
    sender_display_name: config.sender_display_name,
    reply_to_email: config.reply_to_email,
    past_due_threshold_days: config.past_due_threshold_days,
    past_due_grace_days: config.past_due_grace_days,
    friendly_threshold_days: config.friendly_threshold_days,
    second_notice_threshold_days: config.second_notice_threshold_days,
    include_residential: config.include_residential,
    sending_enabled: config.sending_enabled,
    // enabled is owned by the bucket endpoint; never send it with a template save.
    email_templates_attributes: config.email_templates.map(({ enabled, ...template }) => template),
  },
});

// Every config save sends the whole form, so a validation error (e.g. inverted thresholds) can surface
// from any of them. Show the API's messages instead of a generic failure so the admin knows what to fix.
const configSaveErrorMessage = (err: unknown, fallback: string): string => {
  const body = (err as AxiosError<{ errors?: string[]; error?: string }>).response?.data;
  if (body?.errors?.length) return `${fallback} ${body.errors.join('. ')}.`;
  return body?.error ? `${fallback} ${body.error}` : fallback;
};

// Only fields the Save button persists, so instant-save toggles (buckets) never count as unsaved.
const savePayloadKey = (config: CollectionConfig) => JSON.stringify(buildSavePayload(config));

const defaultsForFrequency = (
  frequency: CollectionFrequency,
  current: CollectionEmailTemplate,
): Pick<CollectionEmailTemplate, 'schedule_day' | 'schedule_anchor_date' | 'schedule_day_of_month'> => {
  switch (frequency) {
    case 'weekly':
      return {
        schedule_day: current.schedule_day ?? 'Monday',
        schedule_anchor_date: null,
        schedule_day_of_month: null,
      };
    case 'biweekly':
      return {
        schedule_day: current.schedule_day ?? 'Monday',
        schedule_anchor_date: current.schedule_anchor_date,
        schedule_day_of_month: null,
      };
    case 'monthly':
      return {
        schedule_day: null,
        schedule_anchor_date: null,
        schedule_day_of_month: current.schedule_day_of_month ?? 1,
      };
  }
};

interface LocationTabProps {
  location: CorporateLocation;
  enabled: boolean;
}

const LocationTab: React.FC<LocationTabProps> = ({ location: locationKey, enabled }) => {
  const configUrl = `/collections/configs/${locationKey}`;
  const { data, isLoading, isError } = useCollectionConfig(locationKey, enabled);
  const { mutateAsync, isPending } = useUpdateCollectionConfig();
  const { mutateAsync: updateBucket, isPending: isBucketPending } = useUpdateCollectionBucket();
  const queryClient = useQueryClient();
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);

  const [config, setConfig] = useState<CollectionConfig | null>(null);
  const [activeToneTab, setActiveToneTab] = useState(0);
  const [confirmEnableOpen, setConfirmEnableOpen] = useState(false);
  const [savedPayloadKey, setSavedPayloadKey] = useState<string | null>(null);

  useEffect(() => {
    setConfig(null);
  }, [locationKey]);

  useEffect(() => {
    const result = data?.data;
    if (!result) return;
    const normalized = normalizeConfig(result);
    setConfig(normalized);
    setSavedPayloadKey(savePayloadKey(normalized));
  }, [data?.data, locationKey]);

  const persistConfig = useCallback(
    async (nextConfig: CollectionConfig) => {
      const response = await mutateAsync({
        url: configUrl,
        data: buildSavePayload(nextConfig),
      });
      const saved = response.data ? normalizeConfig(response.data) : nextConfig;
      setConfig(saved);
      setSavedPayloadKey(savePayloadKey(saved));
      await queryClient.invalidateQueries({ queryKey: [configUrl] });
      return saved;
    },
    [configUrl, mutateAsync, queryClient],
  );

  if (isLoading) return <Box sx={{ p: 4, textAlign: 'center' }}>Loading...</Box>;
  if (isError) return <ErrorMessage message="Failed to load configuration." />;
  if (!config) return null;

  const hasUnsavedChanges = savedPayloadKey !== null && savePayloadKey(config) !== savedPayloadKey;

  const activeTemplate = config.email_templates.find((t) => t.tone === TONES[activeToneTab]);

  const updateField = (field: keyof CollectionConfig, value: unknown) =>
    setConfig((prev) => (prev ? { ...prev, [field]: value } : prev));

  const updateTemplate = (
    tone: CollectionEmailTemplate['tone'],
    field: keyof CollectionEmailTemplate,
    value: string | number | null,
  ) =>
    setConfig((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        email_templates: prev.email_templates.map((t) => (t.tone === tone ? { ...t, [field]: value } : t)),
      };
    });

  const updateFrequency = (tone: CollectionEmailTemplate['tone'], frequency: CollectionFrequency) =>
    setConfig((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        email_templates: prev.email_templates.map((t) => {
          if (t.tone !== tone) return t;
          return { ...t, frequency, ...defaultsForFrequency(frequency, t) };
        }),
      };
    });

  const handleCopyVariable = async (variable: string) => {
    try {
      await navigator.clipboard.writeText(variable);
      showSuccessSnackBar?.(`Copied ${variable}`);
    } catch {
      showErrorSnackBar?.('Failed to copy to clipboard.');
    }
  };

  const handleSave = async () => {
    try {
      await persistConfig(config);
      showSuccessSnackBar?.('Configuration saved.');
    } catch (err) {
      showErrorSnackBar?.(configSaveErrorMessage(err, 'Failed to save configuration.'));
    }
  };

  const handleIncludeResidentialChange = async (checked: boolean) => {
    const previous = config.include_residential;
    const nextConfig = { ...config, include_residential: checked };
    setConfig(nextConfig);
    try {
      await persistConfig(nextConfig);
      void queryClient.invalidateQueries({
        queryKey: collectionCandidatesQueryKey(locationKey),
      });
      showSuccessSnackBar?.('Client eligibility updated.');
    } catch (err) {
      setConfig({ ...config, include_residential: previous });
      showErrorSnackBar?.(configSaveErrorMessage(err, 'Failed to update client eligibility.'));
    }
  };

  const handleBucketToggle = async (tone: CollectionTone, enabled: boolean) => {
    const setEnabled = (value: boolean) =>
      setConfig((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          email_templates: prev.email_templates.map((t) => (t.tone === tone ? { ...t, enabled: value } : t)),
        };
      });
    setEnabled(enabled);
    try {
      const response = await updateBucket({ url: `${configUrl}/buckets/${tone}`, data: { bucket: { enabled } } });
      if (response.data) {
        setConfig((prev) => (prev ? { ...prev, bucket_client_counts: response.data.bucket_client_counts } : prev));
      }
      void queryClient.invalidateQueries({ queryKey: collectionCandidatesQueryKey(locationKey) });
      showSuccessSnackBar?.(`${TONE_LABELS[tone]} bucket turned ${enabled ? 'on' : 'off'}.`);
    } catch {
      setEnabled(!enabled);
      showErrorSnackBar?.(`Failed to update the ${TONE_LABELS[tone]} bucket.`);
    }
  };

  const persistSendingEnabled = async (next: boolean) => {
    const previous = config.sending_enabled;
    const nextConfig = { ...config, sending_enabled: next };
    setConfig(nextConfig);
    try {
      await persistConfig(nextConfig);
      showSuccessSnackBar?.(next ? 'Live email sending enabled.' : 'Live email sending disabled.');
    } catch (err) {
      setConfig({ ...config, sending_enabled: previous });
      showErrorSnackBar?.(configSaveErrorMessage(err, 'Failed to update email sending.'));
    }
  };

  const handleSendingToggle = (checked: boolean) => {
    if (checked) {
      setConfirmEnableOpen(true); // turning ON is high-stakes — confirm first
    } else {
      void persistSendingEnabled(false); // turning OFF is always safe — apply immediately
    }
  };

  return (
    <LoadingWrapper isLoading={isPending} message="Saving...">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Grid container spacing={2}>
          {/* Email Sending (live mode) */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Email Sending
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {!config.sending_enabled && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Email sending is OFF for {locationKey}. No scheduled, manual, or one-off collection emails will be
                  delivered until this is turned on.
                </Alert>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={config.sending_enabled}
                    onChange={(e) => handleSendingToggle(e.target.checked)}
                    color="primary"
                    disabled={isPending}
                  />
                }
                label={
                  <Box>
                    <Typography component="span">Live email sending for {locationKey}</Typography>
                    <Typography variant="body2" color="textSecondary" display="block">
                      When on, collection emails are delivered to clients. When off, nothing is sent.
                    </Typography>
                  </Box>
                }
              />
            </Paper>
          </Grid>

          {/* Client Eligibility */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Client Eligibility
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                When off, only commercial clients will receive scheduled emails.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.include_residential}
                    onChange={(e) => handleIncludeResidentialChange(e.target.checked)}
                    color="primary"
                    disabled={isPending}
                  />
                }
                label={
                  <Box>
                    <Typography component="span">Include home services clients in scheduled emails</Typography>
                    <Typography variant="body2" color="textSecondary" display="block">
                      {residentialToggleImpactText(config.residential_client_count, config.include_residential)}
                    </Typography>
                  </Box>
                }
              />
            </Paper>
          </Grid>

          {/* Collection Buckets */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Collection Buckets
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                When a bucket is off, clients in it are not emailed automatically. Manual one-off emails are still
                allowed.
              </Typography>
              <Grid container spacing={2}>
                {TONES.map((tone) => {
                  const template = config.email_templates.find((t) => t.tone === tone);
                  if (!template) return null;
                  return (
                    <Grid item xs={12} sm={6} md={3} key={tone}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={template.enabled}
                            onChange={(e) => handleBucketToggle(tone, e.target.checked)}
                            color="primary"
                            disabled={isBucketPending}
                          />
                        }
                        label={
                          <Box>
                            <Typography component="span" sx={{ color: TONE_COLOR_BY_KEY[tone] }}>
                              {TONE_LABELS[tone]}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" display="block">
                              {bucketClientCountText(config.bucket_client_counts[tone])}
                            </Typography>
                          </Box>
                        }
                      />
                      {tone === 'past_due' && (
                        <TextField
                          label="Grace period (days)"
                          type="number"
                          size="small"
                          fullWidth
                          sx={{ mt: 1 }}
                          inputProps={{ min: 0 }}
                          value={config.past_due_grace_days}
                          helperText="0 = off. Clients stay in Past Due; emails start after this many days."
                          onChange={(e) => {
                            const parsed = parseInt(e.target.value, 10);
                            if (!Number.isNaN(parsed)) updateField('past_due_grace_days', parsed);
                          }}
                        />
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* Tone Thresholds */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Tone Thresholds
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Days past due determine which email tone a client receives.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Past Due — up to X days"
                type="number"
                fullWidth
                value={config.past_due_threshold_days}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value, 10);
                  if (!Number.isNaN(parsed)) updateField('past_due_threshold_days', parsed);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Friendly — up to X days"
                type="number"
                fullWidth
                value={config.friendly_threshold_days}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value, 10);
                  if (!Number.isNaN(parsed)) updateField('friendly_threshold_days', parsed);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Second Notice — up to X days"
                type="number"
                fullWidth
                value={config.second_notice_threshold_days}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value, 10);
                  if (!Number.isNaN(parsed)) updateField('second_notice_threshold_days', parsed);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Urgent — above Second Notice threshold"
                fullWidth
                disabled
                value={`> ${config.second_notice_threshold_days} days`}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Sender Settings */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Sender Settings
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sender Display Name"
                fullWidth
                value={config.sender_display_name}
                onChange={(e) => updateField('sender_display_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Reply-To Email"
                type="email"
                fullWidth
                value={config.reply_to_email}
                onChange={(e) => updateField('reply_to_email', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Email Templates */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Email Templates
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Tabs value={activeToneTab} onChange={(_, v) => setActiveToneTab(v)} sx={{ mb: 2 }}>
            {TONES.map((tone) => (
              <Tab
                key={tone}
                label={TONE_LABELS[tone]}
                sx={{ color: TONE_COLOR_BY_KEY[tone], '&.Mui-selected': { color: TONE_COLOR_BY_KEY[tone] } }}
              />
            ))}
          </Tabs>

          {!activeTemplate && (
            <Typography color="textSecondary" sx={{ mt: 2 }}>
              No template configured for this tone.
            </Typography>
          )}
          {activeTemplate && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={4}>
                  <TextField
                    select
                    label="Frequency"
                    fullWidth
                    value={activeTemplate.frequency}
                    onChange={(e) => updateFrequency(activeTemplate.tone, e.target.value as CollectionFrequency)}
                  >
                    {FREQUENCIES.map((freq) => (
                      <MenuItem key={freq} value={freq}>
                        {FREQUENCY_LABELS[freq]}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {activeTemplate.frequency !== 'monthly' && (
                  <Grid item xs={6} sm={4}>
                    <TextField
                      select
                      label="Schedule Day"
                      fullWidth
                      value={activeTemplate.schedule_day ?? ''}
                      onChange={(e) => updateTemplate(activeTemplate.tone, 'schedule_day', e.target.value)}
                    >
                      {SCHEDULE_DAYS.map((day) => (
                        <MenuItem key={day} value={day}>
                          {day}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}

                {activeTemplate.frequency === 'monthly' && (
                  <Grid item xs={6} sm={4}>
                    <TextField
                      select
                      label="Day of Month"
                      fullWidth
                      value={activeTemplate.schedule_day_of_month ?? 1}
                      onChange={(e) =>
                        updateTemplate(activeTemplate.tone, 'schedule_day_of_month', parseInt(e.target.value, 10))
                      }
                      helperText="Months with fewer days will send on the last day."
                    >
                      {MONTH_DAYS.map((d) => (
                        <MenuItem key={d} value={d}>
                          {d}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}

                <Grid item xs={6} sm={4}>
                  <TextField
                    label="Schedule Time (HH:MM)"
                    fullWidth
                    value={activeTemplate.schedule_time}
                    onChange={(e) => updateTemplate(activeTemplate.tone, 'schedule_time', e.target.value)}
                    placeholder="09:00"
                  />
                </Grid>

                {activeTemplate.frequency === 'biweekly' && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Anchor Date (first send)"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={activeTemplate.schedule_anchor_date ?? ''}
                      onChange={(e) => updateTemplate(activeTemplate.tone, 'schedule_anchor_date', e.target.value)}
                    />
                    {!weekdayMatchesAnchor(activeTemplate.schedule_day, activeTemplate.schedule_anchor_date) && (
                      <FormHelperText error>
                        Anchor date weekday must match Schedule Day ({activeTemplate.schedule_day}).
                      </FormHelperText>
                    )}
                  </Grid>
                )}
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={12} lg={6}>
                  <TextField
                    label="Email Subject"
                    fullWidth
                    value={activeTemplate.subject}
                    onChange={(e) => updateTemplate(activeTemplate.tone, 'subject', e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      Template Variables — click to copy, then paste into the subject or body
                    </Typography>
                    {TEMPLATE_VARIABLES.map(({ variable, description }) => (
                      <Tooltip key={variable} title={description}>
                        <Chip
                          icon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                          label={variable}
                          onClick={() => handleCopyVariable(variable)}
                          clickable
                          variant="outlined"
                          size="small"
                          sx={{ mr: 1, mb: 0.5 }}
                        />
                      </Tooltip>
                    ))}
                  </Box>

                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Email Body
                  </Typography>
                  <EmailTemplateEditor
                    value={activeTemplate.body}
                    onChange={(value, source) => {
                      // Quill emits 'api' changes when it normalizes loaded HTML; only real edits count.
                      if (source === 'user') updateTemplate(activeTemplate.tone, 'body', value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <EmailTemplatePreview
                    previewUrl={`${configUrl}/preview`}
                    subject={activeTemplate.subject}
                    body={activeTemplate.body}
                    senderDisplayName={config.sender_display_name}
                    replyToEmail={config.reply_to_email}
                    extraParams={{
                      past_due_threshold_days: config.past_due_threshold_days,
                      friendly_threshold_days: config.friendly_threshold_days,
                      second_notice_threshold_days: config.second_notice_threshold_days,
                    }}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </Paper>

        {hasUnsavedChanges && (
          // Sticky so the warning stays visible while editing sections far above the Save button.
          <Alert severity="warning" sx={{ position: 'sticky', bottom: 16, zIndex: 1 }}>
            You have unsaved changes. Click Save Configuration to apply them.
          </Alert>
        )}

        <Box>
          <Button variant="contained" color="primary" onClick={handleSave}>
            Save Configuration
          </Button>
        </Box>
      </Box>
      <Dialog open={confirmEnableOpen} onClose={() => setConfirmEnableOpen(false)}>
        <DialogTitle>Enable live email sending?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will send real collection emails to customers in {locationKey}. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEnableOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setConfirmEnableOpen(false);
              void persistSendingEnabled(true);
            }}
          >
            Enable
          </Button>
        </DialogActions>
      </Dialog>
    </LoadingWrapper>
  );
};

const CollectionConfigPage: React.FC = () => {
  const location = useCorporateLocation('collections');

  if (!location) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <PageHeader mainTitle="Collections — Configuration" titleIcon={<SettingsIcon />} />
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Alert severity="info">No Collections location is assigned to this account.</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle="Collections — Configuration" titleIcon={<SettingsIcon />} />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <LocationTab location={location} enabled />
      </Grid>
    </Grid>
  );
};

export default CollectionConfigPage;
